import { authorizedFetch } from '../api.js';

/**
 * Shared response handling for the two JSON:API services — cloud-discount-service (cds) and
 * the new cloud-core-api.
 *
 * Kept separate from `api.js`'s `request()` because that helper surfaces only `body.message`,
 * while both services return `{errors:[{detail, source}]}`. Reusing it would turn every
 * failure into a bare status text with the actual reason discarded.
 */

/**
 * Pull the most useful message out of a JSON:API error body.
 * @param {object} body parsed response body (shape not guaranteed)
 * @param {Response} response
 * @returns {string}
 */
function errorMessage(body, response) {
    const first = body && Array.isArray(body.errors) ? body.errors[0] : null;
    if (first) {
        const source = first.source?.pointer || first.source?.parameter;
        const detail = first.detail || first.title || response.statusText;
        return source ? `${detail} (${source})` : detail;
    }
    // Micronaut's own error shape, then a last resort.
    return body?.message || body?._embedded?.errors?.[0]?.message || response.statusText;
}

/**
 * Authorized fetch with JSON:API aware parsing. Resolves with the parsed body (undefined for
 * an empty response), rejects with an Error carrying `.response` and `.body`.
 * @param {string} url
 * @param {RequestInit?} init
 * @returns {Promise<any>}
 */
export async function jsonApiRequest(url, init) {
    const response = await authorizedFetch(url, init);
    const contentType = response.headers.get('Content-Type');
    const isJson = Boolean(contentType && contentType.match(/application\/(json|vnd\.api\+json)/g));
    const body = isJson ? await response.json() : undefined;

    if (response.ok) {
        return body;
    }

    const error = new Error(errorMessage(body, response));
    error.response = response;
    error.body = body;
    throw error;
}

/**
 * Read `page[cursor]` out of a JSON:API `links.next` value. Both services return a
 * server-relative path (`/api/v2/...`), so it is parsed against a dummy origin and never fetched
 * as-is: the service's base URL env var may carry its own path prefix that the link does not know
 * about.
 * @param {string|null|undefined} nextLink
 * @returns {string|null}
 */
export function nextPageCursor(nextLink) {
    if (!nextLink) {
        return null;
    }
    try {
        return new URL(nextLink, 'http://link.invalid').searchParams.get('page[cursor]') || null;
    } catch (e) {
        return null;
    }
}

/**
 * Flatten a JSON:API collection envelope — `{data:[{id, attributes:{...}}]}` — into plain
 * `{id, ...attributes}` rows. Both services use this envelope for lists.
 * @param {object} envelope
 * @returns {Array<object>}
 */
export function unwrapList(envelope) {
    const data = envelope?.data;
    if (!Array.isArray(data)) {
        return [];
    }
    return data.map(item => ({ id: item.id, ...(item.attributes || {}) }));
}

/**
 * Flatten a single-resource envelope — `{data:{id, attributes:{...}}}`.
 * @param {object} envelope
 * @returns {object|null}
 */
export function unwrapOne(envelope) {
    const item = envelope?.data;
    return item ? { id: item.id, ...(item.attributes || {}) } : null;
}
